import { DriverDocumentStatus, DriverDocumentInvalidationReasonType } from '@enums/driver.enum';

/**
 * DTO para un documento individual de conductor
 */
export class DriverDocumentDto {
  id: number;
  driver_name: string;
  title: string;
  file: string;
  doc_type: string;
  expiration_date: string | null;
  invalidation_reason: string | null;
  invalidation_reason_type: DriverDocumentInvalidationReasonType | null;
  document_status: DriverDocumentStatus;

  constructor(data: any = {}) {
    this.id = data.id || 0;
    this.driver_name = data.driver_name || '';
    this.title = data.title || '';
    this.file = data.file || '';
    this.doc_type = data.doc_type || '';
    this.expiration_date = data.expiration_date ?? null;
    this.invalidation_reason = data.invalidation_reason ?? null;
    this.invalidation_reason_type = data.invalidation_reason_type ?? null;
    this.document_status = data.document_status || DriverDocumentStatus.PENDING;
  }

  static fromJson(json: any): DriverDocumentDto {
    return new DriverDocumentDto(json);
  }

  toJson(): any {
    return {
      id: this.id,
      driver_name: this.driver_name,
      title: this.title,
      file: this.file,
      doc_type: this.doc_type,
      expiration_date: this.expiration_date,
      invalidation_reason: this.invalidation_reason,
      invalidation_reason_type: this.invalidation_reason_type,
      document_status: this.document_status
    };
  }

  copyWith(updates: Partial<DriverDocumentDto>): DriverDocumentDto {
    return new DriverDocumentDto({
      ...this.toJson(),
      ...updates
    });
  }
}

/**
 * DTO para una versión específica de documentos DNI
 */
export interface DniVersion {
  versionKey: string;
  front_dni: DriverDocumentDto | null;
  back_dni: DriverDocumentDto | null;
}

/**
 * DTO para una versión específica de documentos de carnet de conducir
 */
export interface DrivingLicenseVersion {
  versionKey: string;
  front_driving_license: DriverDocumentDto | null;
  back_driving_license: DriverDocumentDto | null;
}

/**
 * DTO para documentos DNI
 */
export class DriverDniDocumentsDto {
  front_dni: DriverDocumentDto | null;
  back_dni: DriverDocumentDto | null;
  allVersions: DniVersion[];

  constructor(data: any = {}) {
    // Extraer todas las versiones de DNI
    this.allVersions = this.extractAllVersions(data);

    // Mantener compatibilidad: front_dni y back_dni apuntan a la primera versión
    const latestVersion = this.allVersions[0];
    this.front_dni = latestVersion?.front_dni ?? null;
    this.back_dni = latestVersion?.back_dni ?? null;
  }

  /**
   * Extrae todas las versiones de documentos DNI (dni_1, dni_2, etc.)
   * Si no hay versiones, retorna un array vacío
   */
  private extractAllVersions(data: any): DniVersion[] {
    // Si tiene front_dni o back_dni directamente, retornar como una única versión
    if (data.front_dni || data.back_dni) {
      return [{
        versionKey: 'default',
        front_dni: data.front_dni ? DriverDocumentDto.fromJson(data.front_dni) : null,
        back_dni: data.back_dni ? DriverDocumentDto.fromJson(data.back_dni) : null
      }];
    }

    // Buscar versiones (dni_1, dni_2, dni_3, etc.)
    const versionKeys = Object.keys(data)
      .filter(key => key.startsWith('dni_'))
      .sort((a, b) => {
        // Ordenar por número de versión descendente (dni_3, dni_2, dni_1)
        const numA = parseInt(a.split('_')[1]);
        const numB = parseInt(b.split('_')[1]);
        return numB - numA;
      });

    if (versionKeys.length === 0) {
      return [];
    }

    // Retornar todas las versiones encontradas
    return versionKeys.map(versionKey => ({
      versionKey,
      front_dni: data[versionKey].front_dni ? DriverDocumentDto.fromJson(data[versionKey].front_dni) : null,
      back_dni: data[versionKey].back_dni ? DriverDocumentDto.fromJson(data[versionKey].back_dni) : null
    }));
  }

  static fromJson(json: any): DriverDniDocumentsDto {
    return new DriverDniDocumentsDto(json);
  }

  toJson(): any {
    return {
      front_dni: this.front_dni?.toJson() ?? null,
      back_dni: this.back_dni?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.front_dni !== null || this.back_dni !== null;
  }
}

/**
 * DTO para documentos de carnet de conducir
 */
export class DriverDrivingLicenseDocumentsDto {
  front_driving_license: DriverDocumentDto | null;
  back_driving_license: DriverDocumentDto | null;
  allVersions: DrivingLicenseVersion[];

  constructor(data: any = {}) {
    // Extraer todas las versiones de carnet de conducir
    this.allVersions = this.extractAllVersions(data);

    // Mantener compatibilidad: front_driving_license y back_driving_license apuntan a la primera versión
    const latestVersion = this.allVersions[0];
    this.front_driving_license = latestVersion?.front_driving_license ?? null;
    this.back_driving_license = latestVersion?.back_driving_license ?? null;
  }

  /**
   * Extrae todas las versiones de documentos de carnet de conducir (driving_license_1, driving_license_2, etc.)
   * Si no hay versiones, retorna un array vacío
   */
  private extractAllVersions(data: any): DrivingLicenseVersion[] {
    // Si tiene front_driving_license o back_driving_license directamente, retornar como una única versión
    if (data.front_driving_license || data.back_driving_license) {
      return [{
        versionKey: 'default',
        front_driving_license: data.front_driving_license ? DriverDocumentDto.fromJson(data.front_driving_license) : null,
        back_driving_license: data.back_driving_license ? DriverDocumentDto.fromJson(data.back_driving_license) : null
      }];
    }

    // Buscar versiones (driving_license_1, driving_license_2, etc.)
    const versionKeys = Object.keys(data)
      .filter(key => key.startsWith('driving_license_'))
      .sort((a, b) => {
        // Ordenar por número de versión descendente (driving_license_3, driving_license_2, driving_license_1)
        const numA = parseInt(a.split('_')[2]);
        const numB = parseInt(b.split('_')[2]);
        return numB - numA;
      });

    if (versionKeys.length === 0) {
      return [];
    }

    // Retornar todas las versiones encontradas
    return versionKeys.map(versionKey => ({
      versionKey,
      front_driving_license: data[versionKey].front_driving_license ? DriverDocumentDto.fromJson(data[versionKey].front_driving_license) : null,
      back_driving_license: data[versionKey].back_driving_license ? DriverDocumentDto.fromJson(data[versionKey].back_driving_license) : null
    }));
  }

  static fromJson(json: any): DriverDrivingLicenseDocumentsDto {
    return new DriverDrivingLicenseDocumentsDto(json);
  }

  toJson(): any {
    return {
      front_driving_license: this.front_driving_license?.toJson() ?? null,
      back_driving_license: this.back_driving_license?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.front_driving_license !== null || this.back_driving_license !== null;
  }
}

/**
 * DTO para documentos de autónomo
 */
export class DriverSelfEmploymentDocumentsDto {
  self_employment_registration_fee: DriverDocumentDto | null;
  self_employment_payment_certificate: DriverDocumentDto | null;
  reta_registration_resolution: DriverDocumentDto | null;

  constructor(data: any = {}) {
    this.self_employment_registration_fee = data.self_employment_registration_fee ? DriverDocumentDto.fromJson(data.self_employment_registration_fee) : null;
    this.self_employment_payment_certificate = data.self_employment_payment_certificate ? DriverDocumentDto.fromJson(data.self_employment_payment_certificate) : null;
    this.reta_registration_resolution = data.reta_registration_resolution ? DriverDocumentDto.fromJson(data.reta_registration_resolution) : null;
  }

  static fromJson(json: any): DriverSelfEmploymentDocumentsDto {
    return new DriverSelfEmploymentDocumentsDto(json);
  }

  toJson(): any {
    return {
      self_employment_registration_fee: this.self_employment_registration_fee?.toJson() ?? null,
      self_employment_payment_certificate: this.self_employment_payment_certificate?.toJson() ?? null,
      reta_registration_resolution: this.reta_registration_resolution?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.self_employment_registration_fee !== null ||
           this.self_employment_payment_certificate !== null ||
           this.reta_registration_resolution !== null;
  }
}

/**
 * DTO para documentos de cuenta bancaria
 */
export class DriverBankAccountDocumentsDto {
  bank_account_certificate: DriverDocumentDto | null;

  constructor(data: any = {}) {
    this.bank_account_certificate = data.bank_account_certificate ? DriverDocumentDto.fromJson(data.bank_account_certificate) : null;
  }

  static fromJson(json: any): DriverBankAccountDocumentsDto {
    return new DriverBankAccountDocumentsDto(json);
  }

  toJson(): any {
    return {
      bank_account_certificate: this.bank_account_certificate?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.bank_account_certificate !== null;
  }
}

/**
 * DTO para documentos de antecedentes penales
 */
export class DriverCriminalRecordDocumentsDto {
  criminal_record_certificate: DriverDocumentDto | null;

  constructor(data: any = {}) {
    this.criminal_record_certificate = data.criminal_record_certificate ? DriverDocumentDto.fromJson(data.criminal_record_certificate) : null;
  }

  static fromJson(json: any): DriverCriminalRecordDocumentsDto {
    return new DriverCriminalRecordDocumentsDto(json);
  }

  toJson(): any {
    return {
      criminal_record_certificate: this.criminal_record_certificate?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.criminal_record_certificate !== null;
  }
}

/**
 * DTO para documentos de puntos del carné
 */
export class DriverDrivingPointsDocumentsDto {
  driving_points_certificate: DriverDocumentDto | null;

  constructor(data: any = {}) {
    this.driving_points_certificate = data.driving_points_certificate ? DriverDocumentDto.fromJson(data.driving_points_certificate) : null;
  }

  static fromJson(json: any): DriverDrivingPointsDocumentsDto {
    return new DriverDrivingPointsDocumentsDto(json);
  }

  toJson(): any {
    return {
      driving_points_certificate: this.driving_points_certificate?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.driving_points_certificate !== null;
  }
}

/**
 * DTO para documentos fiscales
 */
export class DriverTaxDocumentsDto {
  tax_census_registration_certificate: DriverDocumentDto | null;
  tax_debt_certificate: DriverDocumentDto | null;
  form_036: DriverDocumentDto | null;

  constructor(data: any = {}) {
    this.tax_census_registration_certificate = data.tax_census_registration_certificate ? DriverDocumentDto.fromJson(data.tax_census_registration_certificate) : null;
    this.tax_debt_certificate = data.tax_debt_certificate ? DriverDocumentDto.fromJson(data.tax_debt_certificate) : null;
    this.form_036 = data.form_036 ? DriverDocumentDto.fromJson(data.form_036) : null;
  }

  static fromJson(json: any): DriverTaxDocumentsDto {
    return new DriverTaxDocumentsDto(json);
  }

  toJson(): any {
    return {
      tax_census_registration_certificate: this.tax_census_registration_certificate?.toJson() ?? null,
      tax_debt_certificate: this.tax_debt_certificate?.toJson() ?? null,
      form_036: this.form_036?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.tax_census_registration_certificate !== null ||
           this.tax_debt_certificate !== null ||
           this.form_036 !== null;
  }
}

/**
 * DTO para documentos de seguridad social
 */
export class DriverSocialSecurityDocumentsDto {
  social_security_compliance_certificate: DriverDocumentDto | null;

  constructor(data: any = {}) {
    this.social_security_compliance_certificate = data.social_security_compliance_certificate ? DriverDocumentDto.fromJson(data.social_security_compliance_certificate) : null;
  }

  static fromJson(json: any): DriverSocialSecurityDocumentsDto {
    return new DriverSocialSecurityDocumentsDto(json);
  }

  toJson(): any {
    return {
      social_security_compliance_certificate: this.social_security_compliance_certificate?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.social_security_compliance_certificate !== null;
  }
}

/**
 * DTO para documentos NIE
 */
export class DriverNieDocumentsDto {
  residence_permit_nie: DriverDocumentDto | null;
  work_permit_nie: DriverDocumentDto | null;

  constructor(data: any = {}) {
    this.residence_permit_nie = data.residence_permit_nie ? DriverDocumentDto.fromJson(data.residence_permit_nie) : null;
    this.work_permit_nie = data.work_permit_nie ? DriverDocumentDto.fromJson(data.work_permit_nie) : null;
  }

  static fromJson(json: any): DriverNieDocumentsDto {
    return new DriverNieDocumentsDto(json);
  }

  toJson(): any {
    return {
      residence_permit_nie: this.residence_permit_nie?.toJson() ?? null,
      work_permit_nie: this.work_permit_nie?.toJson() ?? null
    };
  }

  hasDocuments(): boolean {
    return this.residence_permit_nie !== null || this.work_permit_nie !== null;
  }
}

/**
 * DTO para todos los documentos del conductor
 */
export class DriverDocumentsDto {
  dni: DriverDniDocumentsDto;
  driving_license: DriverDrivingLicenseDocumentsDto;
  self_employment: DriverSelfEmploymentDocumentsDto;
  bank_account: DriverBankAccountDocumentsDto;
  criminal_record: DriverCriminalRecordDocumentsDto;
  driving_points: DriverDrivingPointsDocumentsDto;
  tax: DriverTaxDocumentsDto;
  social_security: DriverSocialSecurityDocumentsDto;
  nie: DriverNieDocumentsDto;

  constructor(data: any = {}) {
    this.dni = DriverDniDocumentsDto.fromJson(data.dni || {});
    this.driving_license = DriverDrivingLicenseDocumentsDto.fromJson(data.driving_license || {});
    this.self_employment = DriverSelfEmploymentDocumentsDto.fromJson(data.self_employment || {});
    this.bank_account = DriverBankAccountDocumentsDto.fromJson(data.bank_account || {});
    this.criminal_record = DriverCriminalRecordDocumentsDto.fromJson(data.criminal_record || {});
    this.driving_points = DriverDrivingPointsDocumentsDto.fromJson(data.driving_points || {});
    this.tax = DriverTaxDocumentsDto.fromJson(data.tax || {});
    this.social_security = DriverSocialSecurityDocumentsDto.fromJson(data.social_security || {});
    this.nie = DriverNieDocumentsDto.fromJson(data.nie || {});
  }

  static fromJson(json: any): DriverDocumentsDto {
    return new DriverDocumentsDto(json);
  }

  toJson(): any {
    return {
      dni: this.dni.toJson(),
      driving_license: this.driving_license.toJson(),
      self_employment: this.self_employment.toJson(),
      bank_account: this.bank_account.toJson(),
      criminal_record: this.criminal_record.toJson(),
      driving_points: this.driving_points.toJson(),
      tax: this.tax.toJson(),
      social_security: this.social_security.toJson(),
      nie: this.nie.toJson()
    };
  }
}
